import { z } from 'zod'

import type { ListParams } from './listParams'

/** A `yyyy-mm-dd` day in a search param, or nothing if it is not one. */
export const isoDayParam = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)
  .describe('A day, as YYYY-MM-DD.')

/** A page number in a search param. */
export const pageParam = z
  .number()
  .int()
  .positive()
  .optional()
  .catch(undefined)
  .describe('The page of the list to show, from 1.')

/** A page size in a search param: 1 to 100. */
export const limitParam = z
  .number()
  .int()
  .min(1)
  .max(100)
  .optional()
  .catch(undefined)
  .describe('Rows per page, 1 to 100.')

/** A record id in a search param — the open row, a picked warehouse. */
export const idParam = z
  .number()
  .int()
  .positive()
  .optional()
  .catch(undefined)
  .describe('The id of a record.')

/**
 * The view state every read-only ops list page shares.
 *
 * One schema because these pages ask the same few questions (which warehouse,
 * which party, what text, which window, include voided rows); a page that does
 * not use a field simply never offers the control. Every field is optional with
 * a `.catch`, defaults are applied at read time and pruned on the way out, so a
 * URL carries only what differs.
 */
export const listSearchSchema = z.object({
  warehouse: idParam.describe('The warehouse id the list is narrowed to.'),
  party: idParam.describe(
    'The counterparty id the list is narrowed to: a vendor, retailer, store or provider, per page.',
  ),
  invoice: idParam.describe('The invoice id the list is narrowed to.'),
  q: z.string().optional().catch(undefined).describe('Free text the list is searched by.'),
  kind: z
    .string()
    .optional()
    .catch(undefined)
    .describe('A type code the list is narrowed to, such as a note or shipping type.'),
  from: isoDayParam.describe('The first day of the date window, YYYY-MM-DD, inclusive.'),
  to: isoDayParam.describe('The last day of the date window, YYYY-MM-DD, inclusive.'),
  void: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe('Include voided or unblocked records as well as valid ones.'),
  flag: z.boolean().optional().catch(undefined).describe('A page-specific yes/no filter.'),
  page: pageParam,
  limit: limitParam,
  open: idParam.describe('The id of the record whose detail sheet is open.'),
})

export type ListSearch = z.infer<typeof listSearchSchema>

export const LIST_DEFAULTS = { page: 1, limit: 20 } as const

/** Drop any value that equals its default, or is empty. */
export function withoutListDefaults(search: ListSearch): ListSearch {
  const pruned: ListSearch = {}
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.party !== undefined) pruned.party = search.party
  if (search.invoice !== undefined) pruned.invoice = search.invoice
  if (search.q) pruned.q = search.q
  if (search.kind) pruned.kind = search.kind
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.void) pruned.void = true
  if (search.flag) pruned.flag = true
  if (search.page !== undefined && search.page !== LIST_DEFAULTS.page) pruned.page = search.page
  if (search.limit !== undefined && search.limit !== LIST_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.open !== undefined) pruned.open = search.open
  return pruned
}

export type { ListParams }

/** The first instant of a day, as the list endpoints' datetimes read it. */
export function startOfDay(isoDay: string): string {
  return `${isoDay}T00:00:00.000Z`
}

/** The first instant of the day *after*, so an inclusive `to` date is a half-open bound. */
export function startOfNextDay(isoDay: string): string {
  const next = new Date(`${isoDay}T00:00:00.000Z`)
  next.setUTCDate(next.getUTCDate() + 1)
  return next.toISOString()
}

/** What a list endpoint is asked for. */
export function toListParams(search: ListSearch): ListParams {
  return {
    warehouseId: search.warehouse,
    partyId: search.party,
    invoiceId: search.invoice,
    q: search.q || undefined,
    kind: search.kind || undefined,
    dateFrom: search.from ? startOfDay(search.from) : undefined,
    dateTo: search.to ? startOfNextDay(search.to) : undefined,
    includeVoid: search.void === true,
    flag: search.flag === true,
    page: search.page ?? LIST_DEFAULTS.page,
    limit: search.limit ?? LIST_DEFAULTS.limit,
  }
}
